import { useState, useMemo, useEffect, useCallback } from 'react';
import { AdvancedMarker } from "@vis.gl/react-google-maps";
import { useAppSelector } from '@/store/hooks';
import { selectActiveInfoWindowId } from '@/store/slices/mapUiSlice';
import { selectIsLocationVisited } from '@/store/slices/userSpecificSlice';
import React from 'react';
// Import PointProps from the hook where it's defined and exported
import { PointProps } from '@/hooks/useSupercluster';

// Import renamed FirestoreLocation type and LocationType
import type { LocationType } from '@/types/firestore';

// Serializable version of FirestoreLocation for Redux compatibility
export interface SerializableMarkerLocation {
  id: string;
  name: string;
  address: string;
  coordinates: { latitude: number; longitude: number };
  locationType: LocationType;
  googlePlaceId?: string;
  averageRating?: number;
  ratingCount?: number;
  drinkCount?: number;
  lastSightingDate?: number; // Milliseconds timestamp instead of Timestamp object
}

// Modified props to require only locationId and use Redux for the rest
interface LocationMarkerProps {
    locationId: string;
    pointProps: PointProps; // Receive processed point properties
    lat: number;
    lng: number;
    onClick?: () => void;
    isPotential?: boolean; // Add isPotential flag
    isSelected?: boolean; // Optional isSelected prop
}

const LocationMarkerComponent = ({ 
  locationId, 
  pointProps,
  lat,
  lng,
  onClick,
  isPotential = false, // Default to false
  isSelected:外部isSelected, // Renamed to avoid conflict with internal isSelected
}: LocationMarkerProps) => {
  const [isAnimating, setIsAnimating] = useState(true);
  
  // Get current user info from Redux
  const activeInfoWindowId = useAppSelector(selectActiveInfoWindowId);
  
  // Construct the serializable location data from props inside the component
  const location = useMemo((): SerializableMarkerLocation => ({ // Ensure return type matches
    id: locationId, // Use locationId prop
    name: pointProps.name,
    address: pointProps.address,
    coordinates: { latitude: lat, longitude: lng },
    locationType: pointProps.locationType as LocationType, // Cast if necessary
    averageRating: pointProps.averageRating,
    ratingCount: pointProps.ratingCount,
    drinkCount: pointProps.drinkCount,
    lastSightingDate: pointProps.lastSightingDate,
    // googlePlaceId is not in PointProps currently, add if needed
  }), [locationId, pointProps, lat, lng]); // Dependencies are the incoming props
    
  // Use the selector factory for isVisited
  const isVisitedSelector = useMemo(() => selectIsLocationVisited(location.id), [location.id]);
  const isVisited = useAppSelector(isVisitedSelector);
  
  // Start animation when marker is mounted
  useEffect(() => {
    if (!isAnimating) return; // Skip if already done animating
    
    // Use RAF for smoother animation aligned with browser rendering
    const startTime = performance.now();
    const duration = 300; // Animation duration in ms
    
    const animationFrame = requestAnimationFrame(function animate(time) {
      const elapsed = time - startTime;
      
      if (elapsed >= duration) {
        // Animation complete
        setIsAnimating(false);
        return;
      }
      
      // Continue animation
      requestAnimationFrame(animate);
    });
    
    // Clean up animation frame on unmount
    return () => cancelAnimationFrame(animationFrame);
  }, [isAnimating]);
  
  // Get emoji for marker type directly from location prop
  const getMarkerEmoji = useCallback((type: string | undefined) => {
    switch(type) {
      case 'brewery': return '🍺';
      case 'restaurant': return '🍽️';
      case 'shop': return '🛒';
      case 'other': default: return '📌';
    }
  }, []); // No dependencies needed now

  // Handle click on marker - delegate to parent component
  const handleClick = useCallback(() => {
    if (onClick) onClick();
  }, [onClick]);

  // Get lat/lng from location coordinates
  const position = useMemo(() => ({
    lat: lat, // Use lat prop directly
    lng: lng,  // Use lng prop directly
  }), [lat, lng]); // Dependencies are lat and lng props

  // Determine if marker is selected: use prop if provided, otherwise internal logic
  const isSelected = typeof 外部isSelected === 'boolean' ? 外部isSelected : activeInfoWindowId === location.id;

  // Memoize the entire JSX for the marker to prevent re-renders
  const markerComponent = useMemo(() => (
    <AdvancedMarker 
      position={position} 
      onClick={handleClick} 
      title={location.name}
    >
      <div 
        className="cell"
        style={{
          width: '58px',
          height: '58px',
          borderRadius: '10px',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: isVisited ? 'var(--chart-3)' : 'var(--chart-1)',
          boxShadow: '0 3px 6px rgba(0, 0, 0, 0.2)',
          border: isPotential ? '2px solid #ffde59' : '2px solid white', // Yellow border for potential markers
          // Animation properties
          transform: isAnimating ? 'scale(0)' : 'scale(1)',
          opacity: isAnimating ? 0 : (isPotential ? 0.7 : 1), // Combined opacity logic
          transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.2s ease-in',
          // Add highlight effect when selected
          outline: isSelected ? '3px solid #ff9900' : 'none',
        }}
      >
        <div className="text" style={{ fontSize: '26px', position: 'relative', zIndex: 1 }}>
          {getMarkerEmoji(location.locationType)} 
        </div>
      </div>
    </AdvancedMarker>
  ), [
       position, 
       handleClick, 
       location.name, 
       location.locationType, 
       isVisited, 
       isAnimating, 
       isSelected, 
       getMarkerEmoji,
       isPotential // Add isPotential as a dependency
     ]);

  return markerComponent;
};

// Memoize the component but with a deeper comparison for pointProps
const LocationMarker = React.memo(LocationMarkerComponent, (prevProps, nextProps) => {
  // First check identity equality for simple props
  if (
    prevProps.locationId !== nextProps.locationId ||
    prevProps.lat !== nextProps.lat ||
    prevProps.lng !== nextProps.lng ||
    prevProps.onClick !== nextProps.onClick ||
    prevProps.isPotential !== nextProps.isPotential
  ) {
    return false; // Definitely changed
  }
  
  // If pointProps are identical by reference, they're equal
  if (prevProps.pointProps === nextProps.pointProps) {
    return true;
  }
  
  // Otherwise do a deep comparison of critical properties inside pointProps
  // Most importantly check locationType which affects the marker icon
  return (
    prevProps.pointProps.name === nextProps.pointProps.name &&
    prevProps.pointProps.locationType === nextProps.pointProps.locationType &&
    prevProps.pointProps.averageRating === nextProps.pointProps.averageRating
  );
});

LocationMarker.displayName = "LocationMarker";

export default LocationMarker;
